/**
 * READINESS-FE-1 — the readiness list from GET …/readiness.
 * Server order is kept (risks arrive highest severity first).
 * NOT_EVALUABLE is a label, never a count and never a pass.
 */
import type { GateReadiness, ReadinessEntry, ReadinessItem } from './phaseGates.api';

function entryLabel(entry: ReadinessEntry): string {
  if (entry.title && entry.title.trim()) return entry.title;
  if (entry.name && entry.name.trim()) return entry.name;
  return 'Item';
}

function entryKey(entry: ReadinessEntry, index: number): string {
  return entry.taskId || entry.riskId || entry.evidenceId || String(index);
}

function itemBody(item: ReadinessItem): JSX.Element {
  if (item.enforcement === 'NOT_EVALUABLE' || item.state === 'NOT_EVALUABLE') {
    return <p>cannot be evaluated</p>;
  }
  // GATE-DOCS-LABEL-1: documents are advisory (FR-2026-10-15-13). The server's
  // sentence carries the count AND "submission is not blocked by it" — a bare
  // "0 of 2" read exactly like the blocking open-tasks check.
  if (
    item.key === 'REQUIRED_DOCUMENTS' &&
    typeof item.expected === 'number' &&
    !(item.detail && item.detail.trim())
  ) {
    const attached = typeof item.attached === 'number' ? item.attached : 0;
    return (
      <p>
        {attached} of {item.expected} expected. Shown for the reviewer — submission is not blocked by it.
      </p>
    );
  }
  return <p>{item.detail}</p>;
}

/** Visible enforcement tag — the data attribute alone told a sighted user nothing. */
const ENFORCEMENT_TAG: Record<ReadinessItem['enforcement'], { text: string; className: string }> = {
  BLOCKING: { text: 'Blocking', className: 'border-amber-300 bg-amber-50 text-amber-900' },
  ADVISORY: { text: 'Advisory', className: 'border-gray-300 bg-gray-50 text-gray-700' },
  NOT_EVALUABLE: { text: 'Not evaluable', className: 'border-gray-300 bg-white text-gray-600' },
};

const ITEM_FRAME: Record<ReadinessItem['enforcement'], string> = {
  BLOCKING: 'border-amber-300',
  ADVISORY: 'border-dashed border-gray-300',
  NOT_EVALUABLE: 'border-dashed border-gray-300',
};

export function GateReadinessSection({
  readiness,
}: {
  readiness: GateReadiness;
}): JSX.Element | null {
  if (!Array.isArray(readiness.items) || readiness.items.length === 0) return null;
  return (
    <section className="mt-6 space-y-3" data-testid="gate-readiness">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-700">
        Readiness
      </h2>
      {readiness.items.map((item) => (
        <div
          key={item.key}
          data-testid={`gate-readiness-${item.key}`}
          data-enforcement={item.enforcement}
          className={`rounded border p-3 text-sm text-gray-900 ${ITEM_FRAME[item.enforcement] ?? 'border-gray-200'}`}
        >
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-medium">{item.label}</p>
            {ENFORCEMENT_TAG[item.enforcement] ? (
              <span
                data-testid={`gate-readiness-tag-${item.key}`}
                className={`rounded border px-1.5 py-0.5 text-xs font-medium ${ENFORCEMENT_TAG[item.enforcement].className}`}
              >
                {ENFORCEMENT_TAG[item.enforcement].text}
              </span>
            ) : null}
          </div>
          <div className="mt-1 text-gray-700">{itemBody(item)}</div>
          {item.entries.length > 0 ? (
            <ul className="mt-2 list-disc space-y-1 pl-5">
              {item.entries.map((entry, index) => {
                const label = entryLabel(entry);
                const href = typeof entry.link === 'string' && entry.link ? entry.link : null;
                return (
                  <li key={entryKey(entry, index)}>
                    {href ? (
                      <a href={href} className="text-indigo-700 underline">
                        {label}
                      </a>
                    ) : (
                      <span>{label}</span>
                    )}
                  </li>
                );
              })}
            </ul>
          ) : null}
        </div>
      ))}
    </section>
  );
}
